import { Workflow, WorkflowNode } from '../models';
import { DEFAULT_WORKFLOW_ORIENTATION } from './workflow-layout';
import type {
  CanvasViewport,
  LayoutSlot,
  NodeSize,
  WorkflowOrientation
} from './workflow-layout';

/**
 * Canvas position for a node. UI-only state — deliberately kept out of the
 * persisted domain model (WorkflowNode), keyed by node id.
 */
export interface NodePosition {
  x: number;
  y: number;
}

/**
 * Locally persisted snapshot of the workflow being edited (session state).
 * Positions ride along because the canvas layout has no server-side home yet;
 * when the API gains position fields this shape stays the same and the
 * persistence service simply becomes a cache instead of the only copy.
 */
export interface WorkflowDraft {
  workflow: Workflow;
  nodes: WorkflowNode[];
  positions: Record<string, NodePosition>;
  /** Mirrors WorkflowDesignerState.layoutSlots; absent on older drafts. */
  layoutSlots?: Record<string, LayoutSlot>;
  /** Layout scheme the positions were saved under (WORKFLOW_LAYOUT_VERSION). */
  layoutVersion?: number;
  /**
   * Orientation the positions were laid out under. Absent on older drafts.
   * Checked on restore because the preference is per-user, not per-draft: it
   * can change while a draft sits in storage.
   */
  layoutOrientation?: WorkflowOrientation;
  /** Mirrors WorkflowDesignerState.isPersisted at the time of the snapshot. */
  isPersisted: boolean;
  /** Node ids that exist server-side at the time of the snapshot. */
  persistedNodeIds?: string[];
  savedAt: string;
}

/**
 * Workflow designer state interface.
 */
export interface WorkflowDesignerState {
  /** The workflow currently being edited */
  currentWorkflow: Workflow | null;
  /** Nodes of the current workflow */
  nodes: WorkflowNode[];
  /** Canvas positions keyed by node id (UI-only) */
  positions: Record<string, NodePosition>;
  /**
   * Column/row of each node placed by auto-layout. Lets the canvas re-space
   * those cards once it has measured them; dragged nodes are marked pinned.
   */
  layoutSlots: Record<string, LayoutSlot>;
  /** Which way the canvas lays the graph out; mirrors the user's preference. */
  orientation: WorkflowOrientation;
  /** Size of the visible canvas, reported by it; null until it first renders. */
  canvasViewport: CanvasViewport | null;
  /**
   * Card sizes as the canvas rendered them, keyed by node id. Kept so a
   * re-layout that isn't triggered by a resize — turning the canvas — can place
   * cards by their real sizes instead of dropping back to estimates.
   */
  measuredSizes: Record<string, NodeSize>;
  /** Id of the node selected in the canvas/properties panel */
  selectedNodeId: string | null;
  /** True once `currentWorkflow` has been persisted to the API at least once. */
  isPersisted: boolean;
  /**
   * Ids of nodes that exist server-side (captured at load, refreshed after
   * save). Drives the create/update/delete diff on save.
   */
  persistedNodeIds: string[];
  /** True while a save operation is in flight. */
  saving: boolean;
  /** Loading state for the current workflow */
  loading: boolean;
  /** Error state for the current workflow */
  error: string | null;
  /** Workflows list */
  workflows: Workflow[];
  /** Loading state for the workflows list */
  loadingWorkflows: boolean;
  /** Error state for the workflows list */
  workflowsError: string | null;
  /** Workflow whose nodes are loaded for preview — never the one being edited. */
  previewWorkflowId: string | null;
  /** Nodes of the previewed workflow */
  previewNodes: WorkflowNode[];
  /** Loading state for the preview */
  loadingPreview: boolean;
  /** Error state for the preview */
  previewError: string | null;
}

/**
 * Initial state.
 */
export const initialState: WorkflowDesignerState = {
  currentWorkflow: null,
  nodes: [],
  positions: {},
  layoutSlots: {},
  orientation: DEFAULT_WORKFLOW_ORIENTATION,
  canvasViewport: null,
  measuredSizes: {},
  selectedNodeId: null,
  isPersisted: false,
  persistedNodeIds: [],
  saving: false,
  loading: false,
  error: null,
  workflows: [],
  loadingWorkflows: false,
  workflowsError: null,
  previewWorkflowId: null,
  previewNodes: [],
  loadingPreview: false,
  previewError: null
};
